import type { FlowDefinition } from '../types/index.js';
import {
  triggerNode,
  llmNode,
  approvalNode,
  agentTaskNode,
  actionNode,
  edge,
  conditionalEdge,
} from '../helpers.js';
import { SENTRY_INVESTIGATE_PROMPT } from './prompts/sentry-investigate.js';
import { SENTRY_TRIAGE_PROMPT } from './prompts/sentry-triage.js';
import { SENTRY_RESOLVE_PROMPT } from './prompts/sentry-resolve.js';
import { FLOW_TEMPLATE_SYSTEM_ORG_ID } from './constants.js';

/**
 * Sentry Triage Flow: Human-in-the-loop error triage and resolution.
 *
 * Graph:
 *   trigger → summarize → triage (Investigate/Ignore)
 *     →(approved)→ investigate → human-review
 *       →(ignore_in_sentry)→ ignore-in-sentry
 *       →(needs_fix)→ resolve → resolve-review
 *         →(mark_resolved)→ mark-resolved-in-sentry → slack-notification
 *         →(ignore)→ (end)
 *       →(end_flow)→ (end)
 *     →(rejected)→ (end)
 *
 * Trigger: the normalized `sentry/sentry_issue` event (from Sentry webhook)
 *
 * The flow starts by summarizing the Sentry issue, then presents it to
 * the team inbox for triage. If the engineer chooses "Investigate", a
 * single agent investigates and recommends a next step, but the final
 * decision still happens in human review.
 */
export function createSentryTriageFlow(id: string = 'sentry-triage-flow'): FlowDefinition {
  const now = Date.now();

  return {
    id,
    name: 'Sentry Triage',
    description:
      'Sentry issue triage: summarize error, prompt team to Investigate or Ignore, then investigate, route, ignore, or fix.',
    nodes: [
      triggerNode(
        'trigger',
        'On Sentry Issue',
        { kind: 'event', source: 'sentry', event: 'sentry_issue' },
        { position: { x: -300, y: 0 } },
      ),

      llmNode(
        'summarize',
        'Summarize Issue',
        {
          promptTemplate: SENTRY_TRIAGE_PROMPT,
          responseFormat: 'text',
          outputKey: 'triageSummary',
          stateOutputMapping: {
            triageSummary: 'triageSummary',
          },
        },
        {
          position: { x: 0, y: 0 },
        },
      ),

      approvalNode(
        'triage',
        'Investigate or Ignore',
        {
          approvalType: 'generic',
          prompt:
            'A new Sentry issue was detected. Review the summary below and decide whether to investigate (approve) or ignore (reject) this issue.\n\n{{state.triageSummary}}',
          allowFeedback: true,
        },
        {
          position: { x: 300, y: 0 },
        },
      ),

      agentTaskNode(
        'investigate',
        'Investigate Issue',
        {
          promptTemplate: SENTRY_INVESTIGATE_PROMPT,
          repoSource: 'trigger',
          responseFormat: 'json',
          outputKey: 'result',
          jsonSchema: {
            type: 'object',
            properties: {
              summary: { type: 'string' },
              recommendation: {
                type: 'string',
                enum: ['needs_human', 'safe_ignore', 'needs_fix'],
              },
              reasoning: { type: 'string' },
              fixPlan: { type: 'string' },
              ignoreReason: { type: 'string' },
              handoff: { type: 'string' },
            },
            required: ['summary', 'recommendation', 'reasoning'],
          },
          useMemory: true,
          saveToMemory: true,
          stateOutputMapping: {
            investigationSummary: 'summary',
            investigationRecommendation: 'recommendation',
            investigationReasoning: 'reasoning',
            investigationFixPlan: 'fixPlan',
            investigationIgnoreReason: 'ignoreReason',
            investigationHandoff: 'handoff',
          },
        },
        {
          position: { x: 600, y: 0 },
        },
      ),

      approvalNode(
        'human-review',
        'Human Review',
        {
          approvalType: 'generic',
          prompt:
            'Review the investigation below and choose whether to ignore the issue in Sentry, proceed with a fix, or end the flow.\n\nRecommended action: {{nodes.investigate.output.recommendation}}\n\nSummary: {{nodes.investigate.output.summary}}\n\nReasoning: {{nodes.investigate.output.reasoning}}\n\n{{#if nodes.investigate.output.fixPlan}}Proposed fix plan: {{nodes.investigate.output.fixPlan}}\n\n{{/if}}{{#if nodes.investigate.output.ignoreReason}}Ignore reason: {{nodes.investigate.output.ignoreReason}}\n\n{{/if}}{{#if nodes.investigate.output.handoff}}Handoff: {{nodes.investigate.output.handoff}}{{/if}}',
          allowFeedback: true,
          decisionOptions: [
            { value: 'ignore_in_sentry', label: 'Ignore in Sentry' },
            { value: 'needs_fix', label: 'Fix' },
            { value: 'end_flow', label: 'Do nothing / End flow' },
          ],
        },
        {
          position: { x: 900, y: 0 },
        },
      ),

      actionNode(
        'ignore-in-sentry',
        'Ignore in Sentry',
        {
          provider: 'sentry',
          operation: 'update_issue',
          params: {
            issueId: '{{trigger.sentryIssueId}}',
            status: 'ignored',
            statusReason: '{{nodes.investigate.output.ignoreReason}}',
          },
          stateOutputMapping: {
            sentryIssueStatus: 'status',
          },
        },
        {
          position: { x: 1200, y: -120 },
        },
      ),

      agentTaskNode(
        'resolve',
        'Resolve Issue',
        {
          promptTemplate: SENTRY_RESOLVE_PROMPT,
          repoSource: 'trigger',
          responseFormat: 'json',
          outputKey: 'result',
          jsonSchema: {
            type: 'object',
            properties: {
              status: {
                type: 'string',
                enum: ['pr_submitted', 'something_went_wrong'],
              },
              pr: { type: 'string' },
              message: { type: 'string' },
            },
            required: ['status', 'message'],
          },
          useMemory: true,
          saveToMemory: true,
          stateOutputMapping: {
            resolveStatus: 'status',
            resolvePr: 'pr',
            resolveMessage: 'message',
          },
        },
        {
          position: { x: 1200, y: 120 },
        },
      ),

      approvalNode(
        'resolve-review',
        'Review Fix',
        {
          approvalType: 'generic',
          prompt:
            'Agent has submitted PR for fix: {{nodes.resolve.output.pr}}\nSentry issue: {{trigger.issueUrl}}\nMessage: {{nodes.resolve.output.message}}',
          allowFeedback: false,
          decisionOptions: [
            { value: 'ignore', label: 'Ignore' },
            { value: 'mark_resolved', label: 'Mark resolved' },
          ],
        },
        {
          position: { x: 1500, y: 120 },
        },
      ),

      actionNode(
        'mark-resolved-in-sentry',
        'Mark Resolved in Sentry',
        {
          provider: 'sentry',
          operation: 'update_issue',
          params: {
            issueId: '{{trigger.sentryIssueId}}',
            status: 'resolved',
            statusReason: '{{nodes.resolve.output.message}}',
          },
          stateOutputMapping: {
            sentryIssueStatus: 'status',
          },
        },
        {
          position: { x: 1800, y: 120 },
        },
      ),

      actionNode(
        'slack-notification',
        'Slack Notification',
        {
          provider: 'slack',
          operation: 'post_to_channel',
          params: {
            channel: 'backyardigans',
            message:
              "Sentry issue '{{trigger.issueTitle}} ({{trigger.sentryIssueId}})' resolved - PR {{nodes.resolve.output.pr}}",
          },
        },
        {
          position: { x: 2100, y: 120 },
        },
      ),
    ],
    edges: [
      edge('e-trigger-to-summarize', 'trigger', 'summarize'),
      edge('e-summarize-to-triage', 'summarize', 'triage'),
      conditionalEdge('e-triage-investigate', 'triage', 'investigate', 'approved', 'Investigate'),
      edge('e-investigate-to-human-review', 'investigate', 'human-review'),
      conditionalEdge(
        'e-human-review-ignore',
        'human-review',
        'ignore-in-sentry',
        'ignore_in_sentry',
        'Ignore in Sentry',
      ),
      conditionalEdge('e-human-review-fix', 'human-review', 'resolve', 'needs_fix', 'Fix'),
      edge('e-resolve-to-review', 'resolve', 'resolve-review'),
      conditionalEdge(
        'e-resolve-review-mark-resolved',
        'resolve-review',
        'mark-resolved-in-sentry',
        'mark_resolved',
        'Mark resolved',
      ),
      edge('e-mark-resolved-to-slack', 'mark-resolved-in-sentry', 'slack-notification'),
    ],
    enabled: true,
    organizationId: FLOW_TEMPLATE_SYSTEM_ORG_ID,
    createdAt: now,
    updatedAt: now,
  };
}
